import { CircularProgress, Tab, Tabs } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import { FC, useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  circleApi,
  FutureMembershipDetails,
  TribeMembershipDetails,
} from 'src/api/circles';
import { useScopedBranding } from 'src/branding';
import { MuiModal } from 'src/components/material/MuiModal';
import useAppSelector from 'src/hooks/Redux/useAppSelector';
import { SendResponseModal } from 'src/pages/Profile/Tabs/Modals/SendResponseModal/SendResponseModal';
import { selectCurrentUser } from 'src/redux/slices/loggedUserSlice';
import { themeColors } from 'src/theme';
import avatar from '../../../assets/images/logos/default-profile.png';
import { AddMember } from './AddMember/AddMember';
import { BulkAddMembersModal } from './AddMember/BulkAddMember';
import styles from './AddMembersModal.module.scss';
import { removalState, removeConfirmText } from './circleMembership';
import confirmStyles from '../UserInfo/Modals/UnsubscribeFromCircle/UnsubscribeFromCircle.module.scss';

interface AddMembersModalProps {
  isOpenModal: boolean;
  setIsOpenModal: (open: boolean) => void;
  circleId: string;
  tribeName: string;
  isLeader: boolean;
  leaderId: string;
  leaderName: string;
  account_type: string;
  /** Called after a member was removed, so the circle header can refresh its count. */
  onMembersChanged?: () => void;
}

export const AddMembersModal: FC<AddMembersModalProps> = ({
  isOpenModal,
  setIsOpenModal,
  circleId,
  tribeName,
  isLeader,
  leaderId,
  leaderName,
  account_type,
  onMembersChanged,
}) => {
  // In a branded circle people without a photo show their initial instead of
  // the Dunbar placeholder image
  const { isBranded } = useScopedBranding();
  const [showAddMember, setShowAddMember] = useState(false);
  const [showBulkAddMembers, setShowBulkAddMembers] = useState(false);
  const [members, setMembers] = useState<TribeMembershipDetails[]>([]);
  const [futureMembers, setFutureMembers] = useState<FutureMembershipDetails[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [selectedMember, setSelectedMember] =
    useState<TribeMembershipDetails | null>(null);
  const [isOpenSendModal, setIsOpenSendModal] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const currentUserId = useAppSelector(selectCurrentUser)?._id;
  const [removeTarget, setRemoveTarget] =
    useState<TribeMembershipDetails | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const members = await circleApi.getMembersOfCircle(circleId);
      setMembers(members);
    } catch (error) {
      console.error('Failed to fetch members:', error);
    } finally {
      setLoading(false);
    }
  }, [circleId]);

  const fetchFutureMembers = useCallback(async () => {
    setLoading(true);
    try {
      const futureMembers = await circleApi.getFutureMembersOfCircle(circleId);
      setFutureMembers(futureMembers);
    } catch (error) {
      console.error('Failed to fetch future members:', error);
    } finally {
      setLoading(false);
    }
  }, [circleId]);

  useEffect(() => {
    if (isOpenModal) {
      fetchMembers();
      fetchFutureMembers();
    }
  }, [isOpenModal, circleId, fetchMembers, fetchFutureMembers]);

  const confirmRemoveMember = async () => {
    if (!removeTarget) {
      return;
    }
    const member = removeTarget;
    const name = member.user?.full_name || 'The member';
    setRemoveTarget(null);

    try {
      setRemovingId(member._id);
      await circleApi.deleteMember(circleId, member._id);
      setMembers((prev) => prev.filter((m) => m._id !== member._id));
      toast.success(`${name} was removed from the Circle`);
      onMembersChanged?.();
    } catch {
      toast.error('Could not remove the member. Please try again.');
    } finally {
      setRemovingId(null);
    }
  };

  const handleOpenSendModal = (member?: TribeMembershipDetails) => {
    if (!member) {
      return;
    }
    setSelectedMember(member);
    setIsOpenSendModal(true);
  };

  return (
    <MuiModal open={isOpenModal} setOpen={setIsOpenModal} width={400}>
      <div>
        {showAddMember ? (
          <AddMember
            setShowAddMember={setShowAddMember}
            circleId={circleId}
            fetchMembers={fetchMembers}
            fetchFutureMembers={fetchFutureMembers}
          />
        ) : showBulkAddMembers ? (
          <BulkAddMembersModal
            setShowBulkAddMembers={setShowBulkAddMembers}
            circleId={circleId}
            fetchMembers={fetchMembers}
          />
        ) : (
          <div>
            <div className={styles.title}>Members</div>

            <Tabs
              value={tabIndex}
              onChange={(_, newValue) => setTabIndex(newValue)}
              centered
              TabIndicatorProps={{
                sx: { background: themeColors.text.dark100 },
              }}
              sx={{
                '.MuiTab-root': {
                  textTransform: 'capitalize',
                  fontWeight: 400,
                  fontSize: '16px',
                  color: '#666',
                },
                '& button': { color: themeColors.text.dark70 },
                '& button.Mui-selected': { color: themeColors.text.dark100 },
              }}
            >
              <Tab label={`Members (${members.length})`} />
              <Tab label={`Pending (${futureMembers.length})`} />
            </Tabs>

            <div className={styles.userList}>
              {loading ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: '20px',
                  }}
                >
                  <CircularProgress />
                </div>
              ) : (
                (tabIndex === 0 ? members : futureMembers).map((member) => {
                  const tribeMember =
                    tabIndex === 0 ? (member as TribeMembershipDetails) : null;
                  const futureMember = !tribeMember
                    ? (member as FutureMembershipDetails)
                    : null;

                  const displayName =
                    tribeMember?.user?.full_name ??
                    futureMember?.email ??
                    futureMember?.phone_number ??
                    'Unknown';
                  const roleLabel = member.role
                    ? `${member.role.charAt(0).toUpperCase()}${member.role.slice(1).toLowerCase()}`
                    : 'Member';

                  return (
                    <div className={styles.wrapper} key={member._id}>
                      <div className={styles.user}>
                        <Avatar
                          src={
                            tribeMember?.user?.avatar?.url ??
                            (isBranded ? undefined : avatar)
                          }
                          sx={{ width: 45, height: 45, borderRadius: '50%' }}
                        >
                          {displayName[0]?.toUpperCase() || 'U'}
                        </Avatar>
                        <div>
                          <div className={styles.userTitle}>{displayName}</div>
                          <div className={styles.role}>{roleLabel}</div>
                        </div>
                      </div>
                      {tribeMember && (
                        <div className={styles.iconsWrapper}>
                          <button
                            className={styles.btn}
                            onClick={() => handleOpenSendModal(tribeMember)}
                          >
                            Message
                          </button>
                          {isLeader &&
                            removalState(tribeMember, currentUserId) ===
                              'removable' && (
                              <button
                                className={styles.removeBtn}
                                onClick={() => setRemoveTarget(tribeMember)}
                                disabled={removingId === tribeMember._id}
                                aria-label={`Remove ${displayName} from Circle`}
                              >
                                {removingId === tribeMember._id
                                  ? 'Removing...'
                                  : 'Remove'}
                              </button>
                            )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {isLeader && account_type !== 'AUCTION' && (
              <div className={styles.actionButtonsContainer}>
                <button
                  onClick={() => setShowAddMember(true)}
                  className={styles.btn}
                >
                  Add Member
                </button>
                <button
                  onClick={() => setShowBulkAddMembers(true)}
                  className={styles.btn}
                  style={{ marginTop: '10px' }}
                >
                  Bulk Add Members
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <MuiModal
        open={!!removeTarget}
        setOpen={(open) => {
          if (!open) {
            setRemoveTarget(null);
          }
        }}
        width={420}
      >
        <div className={confirmStyles.unsubscribeModal}>
          <p className={confirmStyles.confirmText}>
            {removeTarget
              ? removeConfirmText(removeTarget.user?.full_name || 'this member')
              : ''}
          </p>
          <div className={confirmStyles.btns}>
            <button
              className={confirmStyles.btnCancel}
              onClick={() => setRemoveTarget(null)}
            >
              Cancel
            </button>
            <button className={confirmStyles.btn} onClick={confirmRemoveMember}>
              Remove
            </button>
          </div>
        </div>
      </MuiModal>

      {selectedMember && (
        <SendResponseModal
          isOpenModal={isOpenSendModal}
          setIsOpenModal={setIsOpenSendModal}
          leaderId={leaderId}
          leaderName={leaderName}
          circleId={circleId}
          memberName={selectedMember.user.full_name}
          memberId={selectedMember.user._id}
          isLeader={true}
        />
      )}
    </MuiModal>
  );
};
